import { Box, Dialog, Divider, Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch";
import { selectDrillDownCacheValid } from "../../../../store/selectors/currentReportSelectors";
import type { DrillDownPage } from "../../../../store/types/drillDownPageState.types";
import { getActiveLayer } from "../../../../store/utils/getActiveLayer.ts";
import { createDrillDownPageAction } from "../../../../store/utils/storeUtils";
import CollapseContainer from "../../../common/CollapseContainer.tsx";
import PageToolbar from "../../../PageToolbar.tsx";
import DrillDownComponentList from "./DrillDownComponentList";
import DrillDownConfigurationLoader from "./DrillDownConfigurationLoader";
import { DrillDownGridScrollContext, type DrillDownScrollRequest } from "./DrillDownGridScrollContext.tsx";
import DrillDownWrapper from "./DrillDownWrapper.tsx";
import { isMeasureDrillDownInfo, isMetricDrillDownInfo } from "./isMeasureDrillDownInfo.ts";
import { getExtensionLedger } from "./ledger/extensionLedgers.ts";
import Loader from "./Loader";
import PageTitleWrapper from "./PageTitleWrapper.tsx";

interface Props {
  page: DrillDownPage;
  index: number;
}
const DrillDownPageContainer = ({ page, index }: Props) => {
  const dispatch = useAppDispatch();
  const isCacheValid = useSelector(selectDrillDownCacheValid);
  const [fullScreen, setFullScreen] = useState(false);

  const activeLayer = getActiveLayer(page);
  const isCustomColumn = page.layer.subLayers !== undefined;
  const isLedgerOnlyView =
    page.onlyLedger ||
    (activeLayer.info?.combined &&
      isMeasureDrillDownInfo(activeLayer.info.combined) &&
      getExtensionLedger(activeLayer.info.combined.table) !== undefined);
  const isMetricView = !!activeLayer.info?.combined && isMetricDrillDownInfo(activeLayer.info.combined);
  const showHeaderDivider = isLedgerOnlyView || isMetricView;

  const handleDialogClose = useCallback(
    () => dispatch(createDrillDownPageAction("remove", page.pageId)),
    [dispatch, page.pageId]
  );

  const [visibleColumnFields, setVisibleColumnFields] = useState<string[]>([]);
  const [scrollRequest, setScrollRequest] = useState<DrillDownScrollRequest | null>(null);
  const requestScrollToField = useCallback(
    (field: string) => setScrollRequest((prev) => ({ field, nonce: (prev?.nonce ?? 0) + 1 })),
    []
  );
  const scrollContextValue = useMemo(
    () => ({
      visibleColumnFields,
      setVisibleColumnFields,
      scrollRequest,
      requestScrollToField,
    }),
    [visibleColumnFields, scrollRequest, requestScrollToField]
  );

  return (
    <Dialog
      open={true}
      maxWidth="xl"
      fullScreen={fullScreen}
      sx={{ mx: 5 + index * 4 }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: `rgba(0,0,0,${((2 + index) / 10).toFixed(1)})`,
          },
        },
        paper: {
          sx: {
            borderRadius: 0,
            height: "100vh",
            maxHeight: "unset",
            width: "100%",
            transition: "max-width .4s ease",
            margin: "unset",
            overflow: "unset",
          },
        },
      }}
    >
      <Box sx={{ display: "flex", height: "inherit", width: "100%" }}>
        <DrillDownGridScrollContext value={scrollContextValue}>
          <DrillDownConfigurationLoader>
            {isCustomColumn && (
              <CollapseContainer
                collapseElement={
                  <Typography
                    variant="h6"
                    sx={(theme) => ({ color: theme.palette.text.primary, transform: "rotate(90deg)" })}
                  >
                    Formula
                  </Typography>
                }
                expandCollapsePaddingTop={20}
                containerSx={{
                  height: "100%",
                  maxWidth: "400px",
                  ".MuiCollapse-root": {
                    height: "100%",
                    width: "100%",
                  },
                  flex: 1,
                }}
              >
                <DrillDownComponentList page={page} />
              </CollapseContainer>
            )}

            <Stack sx={{ height: "100%", width: "100%", flex: 3 }}>
              <PageToolbar
                title={
                  activeLayer.info ? (
                    <PageTitleWrapper configuration={page} info={activeLayer.info.combined} />
                  ) : (
                    <Box sx={{ minHeight: 60 }} />
                  )
                }
                fullScreen={fullScreen}
                setFullScreen={setFullScreen}
                onClose={handleDialogClose}
              />
              {showHeaderDivider && <Divider orientation="horizontal" flexItem />}
              {activeLayer.info === undefined || !isCacheValid ? (
                <Loader key={page.pageId} configuration={page} />
              ) : (
                <DrillDownWrapper key={page.pageId} configuration={page} info={activeLayer.info} />
              )}
            </Stack>
          </DrillDownConfigurationLoader>
        </DrillDownGridScrollContext>
      </Box>
    </Dialog>
  );
};

export default DrillDownPageContainer;
